import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, Snackbar, Alert, Chip, Box,
    Autocomplete, Typography
} from '@mui/material';
import { useState, useEffect } from 'react';
import permissionProfileStore from '../../Mobx/PermissionProfileStore';
import PermissionRuleTooltip from './PermissionRuleTooltip';

const AddPermissionProfile = ({ open, onClose, onSuccess }) => {
    const [form, setForm] = useState({ name: '', features: [], rules: [] });
    const [ruleParts, setRuleParts] = useState({ collection: '', field: '', action: '' });

    const [featureOptions, setFeatureOptions] = useState([]);
    const [collections, setCollections] = useState([]);
    const [fieldsByCollection, setFieldsByCollection] = useState({});
    const [actions, setActions] = useState([]);

    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --------------------------------------------------
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const options = await permissionProfileStore.fetchPermissionOptions();
                setFeatureOptions(options.features || []);
                setCollections(options.collections || []);
                setActions(options.actions || []);
                setFieldsByCollection(options.fieldsByCollection || {});
            } catch (err) {
                showSnackbar(<>
                    שגיאה בטעינת ההרשאות מהשרת:<br />
                    {err?.toString()}
                </>
                );
            }
        };

        fetchOptions();
    }, []);

    useEffect(() => {
        if (open) {
            setForm({ name: '', features: [], rules: [] });
            setRuleParts({ collection: '', field: '', action: '' });
            setErrors({});
        }
    }, [open]);

    // --------------------------------------------------
    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'שדה חובה';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const handleAddRuleFromParts = () => {
        const { collection, field, action } = ruleParts;
        if (!collection || !field || !action) return;

        const rule = `${collection}.${field}.${action}`;
        if (!form.rules.includes(rule)) {
            setForm(prev => ({
                ...prev,
                rules: [...prev.rules, rule]
            }));
        }

        setRuleParts({ collection: '', field: '', action: '' });
    };

    const handleDeleteItem = (type, item) => {
        setForm(prev => ({
            ...prev,
            [type]: prev[type].filter(i => i !== item)
        }));
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            setIsSubmitting(true);
            const payload = {
                name: form.name,
                permissions: {
                    product: { features: form.features },
                    admin: { rules: form.rules }
                }
            };

            await permissionProfileStore.createProfile(payload);

            onSuccess();
            onClose();
            setForm({ name: '', features: [], rules: [] });
            setRuleParts({ collection: '', field: '', action: '' });
            setErrors({});
        } catch (err) {
            if (err.includes('E11000 duplicate key error'))
                err = 'שם הפרופיל כבר קיים';
            showSnackbar(
                <>
                    שגיאה ביצירת פרופיל:<br />
                    {err?.toString()}
                </>
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>הוספת פרופיל הרשאות</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="שם פרופיל"
                                value={form.name}
                                onChange={e => handleChange('name', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                הוספת גישה לפיצ'רים באתר המוצר
                            </Typography>

                            <Autocomplete
                                multiple
                                options={featureOptions}
                                getOptionLabel={(option) => option}
                                value={form.features}
                                onChange={(event, newValue) => handleChange('features', newValue)}
                                disableCloseOnSelect
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="פיצ'רים"
                                        placeholder="בחר פיצ'רים"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                הוספת גישה לאזורים באתר הניהול
                                <PermissionRuleTooltip />
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Autocomplete
                                    options={collections}
                                    value={ruleParts.collection}
                                    onChange={(e, val) => {
                                        setRuleParts({ collection: val || '', field: '', action: '' });
                                    }}
                                    getOptionLabel={(option) => option === '*' ? 'כל האוספים (*)' : option}
                                    renderOption={(props, option) => (
                                        <li {...props} dir="ltr">{option === '*' ? 'כל האוספים (*)' : option}</li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Collection" fullWidth
                                            inputProps={{ ...params.inputProps, dir: 'ltr', style: { textAlign: 'left' } }} />
                                    )}
                                    sx={{ minWidth: 130, flex: 1 }}
                                />

                                <Autocomplete
                                    options={fieldsByCollection[ruleParts.collection] || []}
                                    value={ruleParts.field}
                                    onChange={(e, val) => setRuleParts(prev => ({ ...prev, field: val || '' }))}
                                    getOptionLabel={(option) => option === '*' ? 'כל השדות (*)' : option}
                                    renderOption={(props, option) => (
                                        <li {...props} dir="ltr">{option === '*' ? 'כל השדות (*)' : option}</li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Field" fullWidth
                                            inputProps={{ ...params.inputProps, dir: 'ltr', style: { textAlign: 'left' } }} />
                                    )}
                                    sx={{ minWidth: 130, flex: 1 }}
                                    disabled={!ruleParts.collection}
                                />

                                <Autocomplete
                                    options={actions}
                                    value={ruleParts.action}
                                    onChange={(e, val) => setRuleParts(prev => ({ ...prev, action: val || '' }))}
                                    getOptionLabel={(option) => option === '*' ? 'כל הפעולות (*)' : option}
                                    renderOption={(props, option) => (
                                        <li {...props} dir="ltr">{option === '*' ? 'כל הפעולות (*)' : option}</li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Action" fullWidth
                                            inputProps={{ ...params.inputProps, dir: 'ltr', style: { textAlign: 'left' } }} />
                                    )}
                                    sx={{ minWidth: 130, flex: 1 }}
                                />

                                <Button
                                    onClick={handleAddRuleFromParts}
                                    variant="outlined"
                                    sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                                >
                                    הוסף חוק
                                </Button>
                            </Box>

                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {form.rules.map((item, idx) => (
                                    <Chip
                                        key={idx}
                                        label={
                                            <span dir="ltr" style={{ textAlign: 'left', display: 'inline-block', width: '100%' }}>
                                                {item}
                                            </span>
                                        }
                                        onDelete={() => handleDeleteItem('rules', item)}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSubmitting}>ביטול</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>שמירה</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={null}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddPermissionProfile;
